import axios from 'axios';

// API endpoints
const PRINTIFY_API_URL = 'https://0dczroxz6h.execute-api.us-east-1.amazonaws.com/printify-api';
const STRIPE_MERCH_API_URL = 'https://shhwfzy0hg.execute-api.us-east-1.amazonaws.com/handle-stripe-merch';

// Cache key for local storage
const MERCHANDISE_CACHE_KEY = 'screamify_merchandise_cache';
const CACHE_EXPIRY_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Store service for merchandise and payment handling
 */
const storeService = {
  /**
   * Get merchandise from Printify API, filtered to specific variants,
   * using LIVE availability data.
   * @returns {Promise<Object>} Product data
   */
  async getMerchandise() {
    try {
      // Fetch live data from the API
      const response = await fetch(PRINTIFY_API_URL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get merchandise: ${errorText}`);
      }
      
      const data = await response.json();

      // --- REINSTATE FILTERING using live data --- 
      const productData = data?.data?.[0]; 
      
      if (!productData) {
        throw new Error('No products available from API');
      }

      if (productData.variants && productData.variants.length > 0) {
        // Define the titles of the variants we want to keep
        const allowedVariantTitles = [
          "Black",
          "Charcoal / Black",
          "Heather Grey / White"
        ];
        
        // Filter the variants based on title, keeping LIVE availability
        const filteredVariants = productData.variants.filter(variant => 
            allowedVariantTitles.some(allowedTitle => {
                // Normalize both titles (lowercase, trim)
                const normalizedApiTitle = variant.title?.toLowerCase().trim();
                const normalizedAllowedTitle = allowedTitle.toLowerCase().trim();
                
                // Check if the API title matches the allowed title OR the allowed title + " / one size"
                return normalizedApiTitle === normalizedAllowedTitle || 
                       normalizedApiTitle === `${normalizedAllowedTitle} / one size`;
            })
        );

        // Update the product data with the filtered variants
        productData.variants = filteredVariants;

        // Filter the color options to match the remaining variants
        if (productData.options && productData.options.length > 0) {
          const colorOption = productData.options.find(option => option.name === 'Colors');
          if (colorOption && colorOption.values) {
            const allowedOptionIds = new Set();
            // Get the color option IDs used by the *filtered* variants
            productData.variants.forEach(variant => {
              if (variant.options && variant.options.length > 0) {
                allowedOptionIds.add(variant.options[0]); // Add the color option ID
              }
            });
            
            // Filter the actual color option values
            const originalOptionCount = colorOption.values.length;
            colorOption.values = colorOption.values.filter(value => 
              allowedOptionIds.has(value.id)
            );
          }
        }
      }

      return productData;
      
    } catch (error) {
      () => {};
      throw error; 
    }
  },
  
  /**
   * Check product and variant availability from Printify
   * @param {string} productId - Product ID to check
   * @param {Array<string>} variantIds - Optional array of specific variant IDs to check
   * @returns {Promise<Object>} Availability data
   */
  async checkAvailability(productId, variantIds = []) {
    try {
      // HARD-CODED RESPONSE (Fallback - Ideally remove this if unused)
      const hardcodedResponse = {
        success: true,
        availability: {
          [productId]: {
            title: "Screamify Dad Hat",
            variants: {
              "7099909405310242829": { id: "7099909405310242829", title: "Black", is_available: false, sku: "7099909405310242829" },
              "6384552355936740295": { id: "6384552355936740295", title: "Black / Charcoal", is_available: false, sku: "6384552355936740295" },
              "13379605394522636732": { id: "13379605394522636732", title: "Charcoal / Black", is_available: true, sku: "13379605394522636732" }
            }
          }
        },
        out_of_stock_count: 2, // Example
        out_of_stock_variants: [] // Example
      };
      return hardcodedResponse;
    } catch (error) {
      () => {};
      throw error;
    }
  },
  
  /**
   * Create a payment intent for merchandise purchase
   * @param {Object} orderData - Order data including product and variant info
   * @returns {Promise<Object>} Payment intent data including client secret
   */
  async createPaymentIntent(orderData) {
    try {
      const { id, selectedVariant, quantity } = orderData;
      
      // Prepare payment intent request
      const payload = {
        operation: 'create_payment_intent',
        product_id: id,
        variant_id: selectedVariant.id,
        price_cents: selectedVariant.price,
        quantity: quantity
      };
      
      // Make API call to our Lambda function
      const response = await fetch(STRIPE_MERCH_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create payment intent: ${errorText}`);
      }
      
      const data = await response.json();
      
      return data;
    } catch (error) {
      () => {};
      throw error;
    }
  },
  
  /**
   * Process a payment with Stripe
   * @param {string} paymentMethodId - Stripe payment method ID
   * @param {Object} paymentData - Payment data including amount
   * @returns {Promise<Object>} Payment result
   */
  async processPayment(paymentInfo) {
    try {
      const payload = {
        operation: 'process_payment',
        ...paymentInfo
      };
      
      // Make API call to our Lambda function
      const response = await fetch(STRIPE_MERCH_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to process payment: ${errorText}`);
      }
      
      const result = await response.json();
      
      // Handle payment result
      if (result.error) {
        throw new Error(result.error.message);
      }
      
      return result;
    } catch (error) {
      () => {};
      throw error;
    }
  },
  
  /**
   * Submit an order to Printify
   * @param {Object} orderData - Order data including product and variant info
   * @returns {Promise<Object>} Order submission result
   */
  async submitOrder(orderData) {
    try {
      const { id, title, selectedVariant, quantity, price, totalPrice, payment_id, order_id, customerInfo } = orderData;
      
       // We rely on the Lambda's check_availability=true flag now
       // No need for frontend check here if Lambda handles it
      
      // Create payload for the Printify API
      const payload = {
        operation: 'create_order',
        external_id: order_id || `SCREAMIFY-${Date.now()}`,
        line_items: [
          {
            product_id: id, // Use the original product ID
            variant_id: selectedVariant.id, // Use the selected variant ID
            quantity: quantity
          }
        ],
        // CRUCIAL: Ensure Lambda checks availability before creating order
        check_availability: true,
        abort_if_unavailable: true
      };
      
      // Add customer information if available
      if (customerInfo) {
        payload.address_to = {
          first_name: customerInfo.fullName.split(' ')[0],
          last_name: customerInfo.fullName.split(' ').slice(1).join(' '),
          email: customerInfo.email,
          phone: customerInfo.phone || '',
          address1: customerInfo.address,
          city: customerInfo.city,
          region: customerInfo.state,
          zip: customerInfo.zip,
          country: customerInfo.country || 'US'
        };
      }
      
      // Make API call to our Lambda function
      const response = await fetch(PRINTIFY_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      let responseData = {};
      try {
        // Try to parse JSON, but also store raw text for debugging
        const rawResponseText = await response.text();
        if (rawResponseText) {
             responseData = JSON.parse(rawResponseText);
        } else {
            responseData = { message: "No content returned" };
        }
      } catch (error) {
         // Use the raw text if JSON parsing fails
         responseData = { error: `Failed to parse response: ${error}`, raw: response.text }; 
      }
      
      if (!response.ok) {
        // Handle specific case for out of stock returned by the Lambda
        if (response.status === 400 && responseData?.error?.includes('out of stock')) {
            const unavailableItemInfo = responseData.unavailable_items && responseData.unavailable_items.length > 0
                ? `: ${responseData.unavailable_items[0].variant_title}`
                : '';
          throw new Error(`Product is out of stock${unavailableItemInfo}`);
        }
        // Throw a generic error using the Lambda response if available
        throw new Error(responseData?.error || `Failed to submit order (Status: ${response.status})`);
      }
      
      return {
        success: true,
        message: 'Order placed successfully!',
        order: {
          orderId: order_id || `SCREAMIFY-${Date.now()}`,
          printify_id: responseData?.order_id || responseData?.data?.id,
        },
        printifyResponse: responseData
      };
    } catch (error) {
      () => {};
      throw error;
    }
  },
  
  /**
   * Confirm payment and submit order
   * @param {Object} paymentInfo - Payment info including payment intent ID
   * @param {Object} orderData - Order data including product and variant info
   * @returns {Promise<Object>} Confirmation result
   */
  async confirmPaymentAndOrder(paymentInfo, orderData) {
    try {
      if (!paymentInfo || !paymentInfo.payment_intent) {
        throw new Error('Missing payment intent ID');
      }
      
      // Record the payment ID in the order data
      const orderWithPayment = {
        ...orderData,
        payment_id: paymentInfo.payment_intent,
        order_id: `order_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`
      };
      
      // Submit the order to Printify
      const result = await storeService.submitOrder(orderWithPayment);
      
      return result;
    } catch (error) {
      () => {};
      throw error;
    }
  }
};

export const {
  getMerchandise,
  checkAvailability, // Keep export JIC, though ideally unused directly now
  submitOrder,
  createPaymentIntent,
  processPayment,
  confirmPaymentAndOrder
} = storeService;

/**
 * Helper function to prefetch and cache product images
 * Call this function when you want to preload product images
 * @param {Object} productData - The product data containing images
 */
export const prefetchProductImages = (productData) => {
  if (!productData || !productData.images || !Array.isArray(productData.images)) {
    return;
  }
  
  // Create Image objects to trigger browser caching
  const preloadImage = (src) => {
    if (!src) return null;
    const img = new Image();
    img.src = src;
    return img;
  };
  
  // Use a small delay between image loads to prioritize visible content first
  let delay = 0;
  const preloadedImages = [];
  
  // First load the default image with priority
  const defaultImage = productData.images.find(img => img.is_default);
  if (defaultImage && defaultImage.src) {
    preloadedImages.push(preloadImage(defaultImage.src));
  }
  
  // Then load the rest with increasing delays
  productData.images.forEach((image, index) => {
    // Skip the default image we've already loaded
    if (defaultImage && image.src === defaultImage.src) {
      return;
    }
    
    // Increase delay for each subsequent image
    delay += 100;
    setTimeout(() => {
      preloadedImages.push(preloadImage(image.src));
    }, delay);
  });
  
  return preloadedImages;
};

export default storeService;
